import { useCallback, useRef, useState } from 'react';
import { Box } from '@mui/material';

/**
 * 두 영역을 나란히(가로) 또는 위아래(세로)로 두고 손잡이로 비율을 조절한다.
 * 가로는 좌 원본/우 편집, 세로는 상 헤더/하 그리드에 쓴다. 더블클릭하면 기본 비율로 돌아간다.
 * firstCollapsed면 첫 영역은 내용 높이(접힌 머리글)만 차지하고 손잡이를 숨겨 나머지를 두 번째 영역에 준다. 펼치면 접기 전 비율로 돌아간다.
 */
export default function SplitPane({ direction = 'horizontal', first, second, initialPercent, minPercent, maxPercent, firstCollapsed = false }) {
    const vertical = direction === 'vertical';
    const initial = initialPercent ?? (vertical ? 38 : 42);
    const min = minPercent ?? (vertical ? 12 : 25);
    const max = maxPercent ?? (vertical ? 82 : 65);
    const containerRef = useRef(null);
    const [pct, setPct] = useState(initial);
    /** 끄는 동안 두 영역이 마우스를 받지 않게 한다. 영역 안 iframe 위에서는 마우스 이동이 창에 오지 않아 손잡이가 멈춘다. */
    const [dragging, setDragging] = useState(false);

    const startDrag = useCallback(
        (e) => {
            e.preventDefault();
            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect) return;
            document.body.style.cursor = vertical ? 'row-resize' : 'col-resize';
            document.body.style.userSelect = 'none';
            setDragging(true);
            const onMove = (ev) => {
                const next = vertical ? ((ev.clientY - rect.top) / rect.height) * 100 : ((ev.clientX - rect.left) / rect.width) * 100;
                setPct(Math.min(max, Math.max(min, next)));
            };
            const onUp = () => {
                setDragging(false);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
            };
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
        },
        [vertical, min, max],
    );

    return (
        <Box ref={containerRef} sx={{ display: 'flex', flexDirection: vertical ? 'column' : 'row', width: '100%', height: '100%', minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
            <Box sx={{ [vertical ? 'height' : 'width']: firstCollapsed ? 'auto' : `${pct}%`, minWidth: 0, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', flexShrink: 0, pointerEvents: dragging ? 'none' : 'auto' }}>{first}</Box>
            <Box
                onMouseDown={startDrag}
                hidden={firstCollapsed}
                onDoubleClick={() => setPct(initial)}
                title="Drag to resize · double-click to reset"
                sx={{ flexShrink: 0, [vertical ? 'height' : 'width']: 8, cursor: vertical ? 'row-resize' : 'col-resize', bgcolor: '#e6ebee', borderRadius: 1, '&:hover': { bgcolor: 'primary.main' }, [vertical ? 'my' : 'mx']: 0.5 }}
            />
            <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', pointerEvents: dragging ? 'none' : 'auto' }}>{second}</Box>
        </Box>
    );
}
